import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';

/**
* AI:
* Status: 'in progress'
* Type: Service
* SubType: WidgetConfig
* Reason: Configured data provider for widget data fetching
*/

import {ActivatedRoute} from '@angular/router';
import {
    CnObjectModeViewBase
} from 'features/feature-prov/cn/shared/cn-object-mode-view.base';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {MvsFormFieldAccessEnum} from 'features/core/shared/logic/form/logic/mvs-form-field-access.enum';

/**
* Shared base for the CommissionRule object-component views (FR-4).
*
* Builds the metadata-driven widgets for each left-nav section and lazily renders them.
* Child entities are created with FK-defaulting on commissionRuleDtoId (READ-ONLY, CONFIG-012).
*
* Sections:
*   cn.CommissionRule              Allgemein (rule master data)
*   cn.CommissionRuleTier          Staffeln       (FK commissionRule)
*   cn.CommissionCondition         Bedingungen    (FK commissionRule)
*   cn.CommissionCap               Cap            (catalog assignment)
*   cn.CommissionLogicHook         LogicHook      (FK commissionRule)
*   cn.CommissionLiabilityTemplate Haftung/Kaution (cn-internal templates)
*   preview                        Vorschau       (cn-rule-preview, FR-17)
*
* NFR-CleanCore: forms are 100% metadata-driven via mvs-widget; NO manual Angular forms.
*/
@Component({
    selector: 'mvs-cn-commission-rule-object-base',
    templateUrl: './cn-commission-rule-object-base-view.component.html',
    styleUrls: ['./cn-commission-rule-object-base-view.component.scss'],
    standalone: false
})
export class CnCommissionRuleObjectBaseViewComponent extends CnObjectModeViewBase
    implements OnInit, OnChanges, OnDestroy {

    @Input() objectIdentifier: ObjectIdentifier;
    @Input() name: string;

    @Output() onNavigationItems: EventEmitter<NavigationItem[]> = new EventEmitter();

    /** Left-nav sections (built by the uiMode-specific view); declared here for G5.1 / G6.1. */
    navigationItems: NavigationItem[];

    busy: boolean;
    initialized: boolean;

    // Section widgets
    ruleWidget: WidgetData;
    tierWidget: WidgetData;
    conditionWidget: WidgetData;
    capWidget: WidgetData;
    logicHookWidget: WidgetData;
    liabilityTemplateWidget: WidgetData;
    cancellationBufferWidget: WidgetData;

    /** Model id resolved from the loaded rule (for cn-rule-preview). */
    commissionModelId: number;

    constructor(protected activatedRoute: ActivatedRoute) {
        super(activatedRoute);
    }

    /**
    * Initialize the view: set the default nav item, emit the nav items to the host object page
    * and build the section widgets. Driven by the uiMode-specific subclass from its ngOnInit();
    * ngOnInit itself is inherited (OBMC route handling) and not overridden here.
    */
    initComponent(): void {
        if (this.navigationItems?.length) {
            this.activeNavigationItem = this.navigationItems[0];
        }
        this.handleNavigationItemsChange();
        this.refreshComponent();
    }

    /**
    * Build all section widgets from the current rule identifier.
    */
    refreshComponent(): void {
        if (!this.objectIdentifier?.objectId) {
            return;
        }
        const ruleId = this.objectIdentifier.objectId;

        this.refreshRuleWidget(ruleId);
        this.refreshTierWidget(ruleId);
        this.refreshConditionWidget(ruleId);
        this.refreshCapWidget(ruleId);
        this.refreshLogicHookWidget(ruleId);
        this.refreshLiabilityTemplateWidget();
        this.refreshCancellationBufferWidget();

        // Resolve the owning model id for the rule preview (FR-17), if the dto is available.
        this.commissionModelId = this.dto?.['commissionModelDtoId'];

        this.initialized = true;
    }

    /**
    * AI: Allgemein — rule master data form (metadata-driven).
    * @widget cn.commission.rule.detail.widget (entity, list)
    */
    refreshRuleWidget(ruleId: number): void {
        this.ruleWidget = WidgetFactory.createWidgetEntityData(
            'cn.commission.rule.detail.widget',
            'Allgemein',
            'cn.CommissionRule',
            ruleId
        );
    }

    /**
    * AI: Staffeln — CommissionRuleTier filtered by rule, FK-defaulted.
    * @widget cn.commission.rule.tier.widget (entity, list)
    */
    refreshTierWidget(ruleId: number): void {
        this.tierWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.rule.tier.widget',
            'Staffeln',
            'cn.CommissionRuleTier',
            [FilterCriteria.create('e.commissionRuleId', FilterCriteria.cOperatorEqual, ruleId)],
            // TM-1000001915 (B2): sort by real entity field orderPriority (e.sortOrder does not exist)
            [new Sorting('e.orderPriority', true)]
        );
        this.tierWidget.functionCallbacks = this.childCreateCallback('commissionRuleDtoId', ruleId);
    }

    /**
    * AI: Bedingungen — CommissionCondition filtered by rule, FK-defaulted.
    * @widget cn.commission.rule.condition.widget (entity, list)
    */
    refreshConditionWidget(ruleId: number): void {
        this.conditionWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.rule.condition.widget',
            'Bedingungen',
            'cn.CommissionCondition',
            [FilterCriteria.create('e.commissionRuleId', FilterCriteria.cOperatorEqual, ruleId)],
            // TM-1000001915 (B2): sort by real entity field orderPriority (e.sortOrder does not exist)
            [new Sorting('e.orderPriority', true)]
        );
        this.conditionWidget.functionCallbacks = this.childCreateCallback('commissionRuleDtoId', ruleId);
    }

    /**
    * AI: Cap — catalog of CommissionCap (assignment of the rule's cap).
    * @widget cn.commission.rule.cap.widget (entity, list)
    */
    refreshCapWidget(ruleId: number): void {
        this.capWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.rule.cap.widget',
            'Caps',
            'cn.CommissionCap',
            [],
            [new Sorting('e.id', false)]
        );
    }

    /**
    * AI: LogicHook — CommissionLogicHook filtered by rule, FK-defaulted.
    * @widget cn.commission.rule.logic.hook.widget (entity, list)
    */
    refreshLogicHookWidget(ruleId: number): void {
        this.logicHookWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.rule.logic.hook.widget',
            'LogicHooks',
            'cn.CommissionLogicHook',
            [FilterCriteria.create('e.commissionRuleId', FilterCriteria.cOperatorEqual, ruleId)],
            // TM-1000001915 (B2): sort by real entity field orderPriority (e.sortOrder does not exist;
            // CommissionCap above keeps e.id as it has no orderPriority)
            [new Sorting('e.orderPriority', true)]
        );
        this.logicHookWidget.functionCallbacks = this.childCreateCallback('commissionRuleDtoId', ruleId);
    }

    /**
    * AI: Haftung — cn CommissionLiabilityTemplate catalog.
    * @widget cn.commission.liability.template.widget (entity, list)
    */
    refreshLiabilityTemplateWidget(): void {
        this.liabilityTemplateWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.liability.template.widget',
            'Haftungsvorlagen',
            'cn.CommissionLiabilityTemplate',
            [],
            [new Sorting('e.id', false)]
        );
    }

    /**
    * AI: Kaution — cn CommissionCancellationBuffer catalog.
    * @widget cn.commission.cancellation.buffer.widget (entity, list)
    */
    refreshCancellationBufferWidget(): void {
        this.cancellationBufferWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.commission.cancellation.buffer.widget',
            'Stornoreserve-Vorlagen',
            'cn.CommissionCancellationBuffer',
            [],
            [new Sorting('e.id', false)]
        );
    }

    /**
    * FK-defaulting callback for child creation (CONFIG-012): pre-fills the FK and
    * marks it READ-ONLY in the create form.
    */
    protected childCreateCallback(fieldName: string, fieldValue: number) {
        const fields: WidgetToolbarCreateInterface[] = [{fieldName, fieldValue}];
        return WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    getNavigationItems(): NavigationItem[] {
        return this.navigationItems;
    }

    handleNavigationItemsChange(): void {
        this.onNavigationItems.emit(this.navigationItems);
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (!this.initialized) {
            return;
        }
        if (changes['objectIdentifier']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
